import React, { useEffect, useRef, useState } from "react"
import { Search } from "react-bootstrap-icons"
import { useHistory } from "react-router"
import { searchSuggestions } from "../../../requests/search"
import type { Suggestion } from "../../../types/search"
import SearchSuggestions from "./SearchSuggestions"

const SCOPE_REGEX =
  /^(bands?|venues?|songs?|albums?|events?|concerts?|posts?):\s*/i
const MAX_SUGGESTIONS = 8

/**
 * Interleave results so that songs etc do not take up the all the suggestions
 * @param groups
 * @param limit
 */
const interleave = (groups: Suggestion[][], limit: number): Suggestion[] => {
  const result: Suggestion[] = []
  const maxLen = groups.reduce((m, g) => Math.max(m, g.length), 0)
  for (let i = 0; i < maxLen && result.length < limit; i++) {
    for (const group of groups) {
      if (i < group.length) {
        result.push(group[i])
        if (result.length >= limit) break
      }
    }
  }
  return result
}

const parseScope = (value: string): { scope: string | null; text: string } => {
  const match = value.match(SCOPE_REGEX)
  if (!match) return { scope: null, text: value }
  return { scope: match[1].toLowerCase(), text: value.slice(match[0].length) }
}

const scopePlaceholder = (scope: string): string =>
  `Search ${scope.endsWith("s") ? scope : `${scope}s`}`

interface SearchFieldProps {
  query?: string
}

const SearchField = ({ query = "" }: SearchFieldProps) => {
  const initial = parseScope(query)
  const [scope, setScope] = useState(initial.scope)
  const [searchField, setSearchField] = useState(initial.text)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const history = useHistory()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const latestQueryRef = useRef("")

  const trimmed = searchField.trim()
  const fullQuery = scope ? `${scope}: ${trimmed}` : trimmed
  const canSuggest = trimmed.length >= 3

  const closeDropdown = () => {
    setIsOpen(false)
    setHighlightedIndex(-1)
  }

  const goToFullSearch = (e?: React.SyntheticEvent) => {
    if (e) e.preventDefault()
    if (!trimmed) return
    closeDropdown()
    history.push(`/search?q=${encodeURIComponent(fullQuery)}`)
  }

  const goToSuggestion = (item: Suggestion) => {
    if (!item || !item.nada_path) return
    closeDropdown()
    history.push(item.nada_path)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (!scope) {
      const parsed = parseScope(value)
      if (parsed.scope) {
        setScope(parsed.scope)
        setSearchField(parsed.text)
        setIsOpen(true)
        return
      }
    }
    setSearchField(value)
    setIsOpen(true)
  }

  useEffect(() => {
    latestQueryRef.current = fullQuery

    if (!canSuggest) {
      setSuggestions([])
      setHasFetched(false)
      closeDropdown()
      return
    }

    const timer = setTimeout(async () => {
      try {
        const data = await searchSuggestions(trimmed, scope ?? undefined)
        if (latestQueryRef.current !== fullQuery) return
        const bands = data?.suggestions?.bands ?? []
        const venues = data?.suggestions?.venues ?? []
        const songs = data?.suggestions?.songs ?? []
        const albums = data?.suggestions?.albums ?? []
        const events = data?.suggestions?.events ?? []
        const posts = data?.suggestions?.posts ?? []
        setSuggestions(
          interleave(
            [bands, venues, songs, albums, events, posts],
            MAX_SUGGESTIONS,
          ),
        )
      } catch (err) {
        if (latestQueryRef.current !== fullQuery) return
        setSuggestions([])
      } finally {
        if (latestQueryRef.current === fullQuery) {
          setHasFetched(true)
        }
      }
    }, 250)

    return () => clearTimeout(timer)
  }, [fullQuery, canSuggest])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        closeDropdown()
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const optionCount = suggestions.length

    const input = e.currentTarget
    const fullSelection =
      searchField.length > 0 &&
      input.selectionStart === 0 &&
      input.selectionEnd === searchField.length
    const isDeletion = e.key === "Backspace" || e.key === "Delete"
    const isTyping = e.key.length === 1 && !e.metaKey && !e.ctrlKey
    if (scope && fullSelection && (isDeletion || isTyping)) {
      setScope(null)
    }

    if (e.key === "ArrowDown") {
      if (!isOpen && canSuggest) setIsOpen(true)
      e.preventDefault()
      if (optionCount === 0) return
      setHighlightedIndex((i) => (i + 1) % optionCount)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      if (optionCount === 0) return
      setHighlightedIndex((i) => (i <= 0 ? optionCount - 1 : i - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        goToSuggestion(suggestions[highlightedIndex])
      } else {
        goToFullSearch(e)
      }
    } else if (e.key === "Escape") {
      closeDropdown()
    } else if (e.key === "Backspace") {
      if (scope && searchField === "") setScope(null)
    }
  }

  const showDropdown =
    isOpen && canSuggest && hasFetched && suggestions.length > 0

  return (
    <div ref={wrapperRef} className="relative">
      <div
        className={`flex items-center bg-white border border-gray-500 ${
          showDropdown ? "rounded-t border-b-0" : "rounded"
        } py-2 px-2 cursor-text`}
        onClick={() => inputRef.current && inputRef.current.focus()}
      >
        {scope && (
          <span className="font-bold text-black text-base leading-tight mr-1">
            {scope}:
          </span>
        )}
        <input
          ref={inputRef}
          type="text"
          name="searchQuery"
          placeholder={scope ? scopePlaceholder(scope) : "Search everything"}
          value={searchField}
          autoComplete="off"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="search-suggestions-listbox"
          aria-autocomplete="list"
          className="appearance-none flex-1 min-w-0 bg-transparent text-base text-black leading-tight focus:outline-none"
          onChange={handleChange}
          onFocus={() => canSuggest && setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <Search
        className="absolute cursor-pointer"
        style={{ top: "12px", right: "8px" }}
        onClick={(e) => goToFullSearch(e)}
      />

      {showDropdown && (
        <SearchSuggestions
          suggestions={suggestions}
          highlightedIndex={highlightedIndex}
          onHighlight={setHighlightedIndex}
          onSelect={goToSuggestion}
        />
      )}
    </div>
  )
}

export default SearchField
